import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Upload, LogOut, ChevronUp, ChevronDown, Edit, Image } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface PortfolioVideo {
  id: string;
  title: string;
  description: string | null;
  industry: string;
  location: string;
  video_url: string;
  thumbnail_url: string | null;
  order_index: number | null;
}

const AdminDashboard = () => {
  const [videos, setVideos] = useState<PortfolioVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("portfolio");

  // Hero images state
  const [heroImages, setHeroImages] = useState([
    { id: 1, name: "Hero Image 1", path: "video-preview-1.jpg" },
    { id: 2, name: "Hero Image 2", path: "video-preview-2.jpg" },
    { id: 3, name: "Hero Image 3", path: "video-preview-3.jpg" },
  ]);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    industry: "",
    location: "",
  });
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkVideos, setBulkVideos] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<Record<string, string>>({});
  
  // Edit state
  const [editingVideo, setEditingVideo] = useState<PortfolioVideo | null>(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    industry: "",
    location: "",
  });
  const [editThumbnailFile, setEditThumbnailFile] = useState<File | null>(null);
  const [generatingThumbnail, setGeneratingThumbnail] = useState<string | null>(null);

  useEffect(() => {
    // Check session on mount
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }
      setLoading(false);
    };
    
    checkAuth();
    fetchVideos();

    // Listen for auth state changes (logout, session expiry)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        navigate("/admin");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from("portfolio_videos")
        .select("*")
        .order("order_index", { ascending: true });

      if (error) throw error;
      setVideos(data || []);
    } catch (error: any) {
      toast.error("Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin");
  };

  const uploadFile = async (file: File, bucket: string, path: string) => {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert: true });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoFile) {
      toast.error("Please select a video file");
      return;
    }

    setUploading(true);

    try {
      const videoPath = `${Date.now()}_${videoFile.name}`;
      const videoUrl = await uploadFile(videoFile, "portfolio-videos", videoPath);

      let thumbnailUrl = null;
      if (thumbnailFile) {
        const thumbnailPath = `${Date.now()}_${thumbnailFile.name}`;
        thumbnailUrl = await uploadFile(thumbnailFile, "portfolio-videos", thumbnailPath);
      }

      const { error } = await supabase.from("portfolio_videos").insert({
        title: formData.title,
        description: formData.description,
        industry: formData.industry,
        location: formData.location,
        video_url: videoUrl,
        thumbnail_url: thumbnailUrl,
        order_index: videos.length,
      });

      if (error) throw error;

      toast.success("Video uploaded successfully!");
      setFormData({ title: "", description: "", industry: "", location: "" });
      setVideoFile(null);
      setThumbnailFile(null);
      fetchVideos();
    } catch (error: any) {
      toast.error(error.message || "Failed to upload video");
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async () => {
    if (bulkVideos.length === 0) {
      toast.error("Please select video files");
      return;
    }

    setUploading(true);
    const progress: Record<string, string> = {};
    
    for (const video of bulkVideos) {
      progress[video.name] = "Uploading...";
      setUploadProgress({ ...progress });

      try {
        const videoPath = `${Date.now()}_${video.name}`;
        const videoUrl = await uploadFile(video, "portfolio-videos", videoPath);

        const title = video.name.replace(/\.[^/.]+$/, "");
        
        const { error } = await supabase.from("portfolio_videos").insert({
          title: title,
          description: "",
          industry: formData.industry || "General",
          location: formData.location || "Unknown",
          video_url: videoUrl,
          thumbnail_url: null,
          order_index: videos.length + Object.keys(progress).filter(k => progress[k] === "✓ Complete").length,
        });

        if (error) throw error;
        
        progress[video.name] = "✓ Complete";
        setUploadProgress({ ...progress });
      } catch (error: any) {
        progress[video.name] = "✗ Failed";
        setUploadProgress({ ...progress });
      }
    }

    setUploading(false);
    toast.success(`Uploaded ${Object.values(progress).filter(v => v === "✓ Complete").length} videos`);
    setBulkVideos([]);
    setUploadProgress({});
    fetchVideos();
  };

  const handleDelete = async (id: string, videoUrl: string, thumbnailUrl: string | null) => {
    if (!confirm("Are you sure you want to delete this video?")) return;

    try {
      const { error } = await supabase
        .from("portfolio_videos")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Delete files from storage
      const videoPath = videoUrl.split("/").pop();
      if (videoPath) {
        await supabase.storage.from("portfolio-videos").remove([videoPath]);
      }
      if (thumbnailUrl) {
        const thumbPath = thumbnailUrl.split("/").pop();
        if (thumbPath) {
          await supabase.storage.from("portfolio-videos").remove([thumbPath]);
        }
      }

      toast.success("Video deleted successfully");
      fetchVideos();
    } catch (error: any) {
      toast.error("Failed to delete video");
    }
  };

  const moveVideo = async (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === videos.length - 1) return;

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const video1 = videos[index];
    const video2 = videos[newIndex];

    try {
      await supabase.from("portfolio_videos").update({ order_index: newIndex }).eq("id", video1.id);
      await supabase.from("portfolio_videos").update({ order_index: index }).eq("id", video2.id);
      
      toast.success("Video order updated");
      fetchVideos();
    } catch (error: any) {
      toast.error("Failed to reorder videos");
    }
  };

  const openEditModal = (video: PortfolioVideo) => {
    setEditingVideo(video);
    setEditFormData({
      title: video.title,
      description: video.description || "",
      industry: video.industry,
      location: video.location,
    });
    setEditThumbnailFile(null);
  };

  const handleEditSubmit = async () => {
    if (!editingVideo) return;
    setUploading(true);

    try {
      let thumbnailUrl = editingVideo.thumbnail_url;
      
      if (editThumbnailFile) {
        const thumbnailPath = `${Date.now()}_${editThumbnailFile.name}`;
        thumbnailUrl = await uploadFile(editThumbnailFile, "portfolio-videos", thumbnailPath);
      }

      const { error } = await supabase
        .from("portfolio_videos")
        .update({
          title: editFormData.title,
          description: editFormData.description,
          industry: editFormData.industry,
          location: editFormData.location,
          thumbnail_url: thumbnailUrl,
        })
        .eq("id", editingVideo.id);

      if (error) throw error;

      toast.success("Video updated successfully");
      setEditingVideo(null);
      fetchVideos();
    } catch (error: any) {
      toast.error("Failed to update video");
    } finally {
      setUploading(false);
    }
  };

  const generateThumbnail = async (videoId: string, videoUrl: string) => {
    setGeneratingThumbnail(videoId);
    
    try {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.src = videoUrl;
      
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          video.currentTime = 1;
        };
        video.onseeked = resolve;
        video.onerror = reject;
      });

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      
      if (!ctx) throw new Error("Failed to get canvas context");
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to create thumbnail"));
        }, "image/jpeg", 0.9);
      });

      const thumbnailPath = `${Date.now()}_thumbnail.jpg`;
      const thumbnailUrl = await uploadFile(
        new File([blob], thumbnailPath, { type: "image/jpeg" }),
        "portfolio-videos",
        thumbnailPath
      );

      const { error } = await supabase
        .from("portfolio_videos")
        .update({ thumbnail_url: thumbnailUrl })
        .eq("id", videoId);

      if (error) throw error;

      toast.success("Thumbnail generated successfully");
      fetchVideos();
    } catch (error: any) {
      toast.error("Failed to generate thumbnail");
    } finally {
      setGeneratingThumbnail(null);
    }
  };

  const handleHeroImageUpload = async (imageIndex: number, file: File) => {
    if (!file) return;

    try {
      setUploadingHeroImage(true);
      const fileName = `hero-${imageIndex}-${Date.now()}.${file.name.split('.').pop()}`;
      const filePath = `hero/${fileName}`;

      const { data, error } = await supabase.storage
        .from("portfolio-videos")
        .upload(filePath, file, { upsert: true });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from("portfolio-videos")
        .getPublicUrl(filePath);

      // Update local state
      setHeroImages(prev => prev.map(img => 
        img.id === imageIndex ? { ...img, path: fileName, url: publicUrl } : img
      ));

      toast.success(`Hero image ${imageIndex} updated! Note: You'll need to update the import in Hero.tsx to use this new image.`);
    } catch (error: any) {
      toast.error(`Failed to upload image: ${error.message}`);
    } finally {
      setUploadingHeroImage(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground">Admin Dashboard</h1>
          <div className="flex gap-2">
            <Button onClick={() => navigate("/admin/orders")} variant="outline">
              Manage Orders
            </Button>
            <Button onClick={() => navigate("/admin/videos")} variant="outline">
              Manage Portfolio Videos
            </Button>
            <Button onClick={handleLogout} variant="outline">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="portfolio">Portfolio Videos</TabsTrigger>
            <TabsTrigger value="hero">Hero Images</TabsTrigger>
          </TabsList>

          {/* Portfolio Videos Tab */}
          <TabsContent value="portfolio" className="space-y-8">
        {/* Upload Form */}
        <div className="bg-card p-6 rounded-xl border border-border mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-heading font-bold">Upload Videos</h2>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setBulkMode(!bulkMode);
                setBulkVideos([]);
                setUploadProgress({});
              }}
            >
              {bulkMode ? "Single Upload" : "Bulk Upload"}
            </Button>
          </div>

          {!bulkMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Physiotherapy Clinic — Neuwied"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Industry</label>
                <Input
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  required
                  placeholder="Healthcare"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location</label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  placeholder="Neuwied, Germany"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="6 videos | 12 posters | +34% inquiries"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Video File (MP4, MOV, WebM)</label>
              <Input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Thumbnail Image (Optional)</label>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
              />
            </div>

              <Button type="submit" disabled={uploading} className="w-full">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : "Upload Video"}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Default Industry (for all videos)</label>
                  <Input
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="Healthcare"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Default Location (for all videos)</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Germany"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Select Multiple Videos (up to 6 recommended)
                </label>
                <Input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setBulkVideos(files);
                  }}
                />
                {bulkVideos.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {bulkVideos.length} video{bulkVideos.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {Object.keys(uploadProgress).length > 0 && (
                <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                  <p className="font-medium text-sm">Upload Progress:</p>
                  {Object.entries(uploadProgress).map(([name, status]) => (
                    <div key={name} className="flex justify-between text-sm">
                      <span className="truncate flex-1">{name}</span>
                      <span className={status.includes("✓") ? "text-green-500" : status.includes("✗") ? "text-red-500" : "text-muted-foreground"}>
                        {status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <Button
                type="button"
                onClick={handleBulkUpload}
                disabled={uploading || bulkVideos.length === 0}
                className="w-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading..." : `Upload ${bulkVideos.length} Video${bulkVideos.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}
        </div>

        {/* Videos List */}
        <div className="bg-card p-6 rounded-xl border border-border">
          <h2 className="text-xl font-heading font-bold mb-4">Uploaded Videos ({videos.length})</h2>
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : videos.length === 0 ? (
            <p className="text-muted-foreground">No videos uploaded yet</p>
          ) : (
            <div className="space-y-4">
              {videos.map((video, index) => (
                <div key={video.id} className="flex items-center gap-4 p-4 border border-border rounded-lg">
                  {/* Reorder buttons */}
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveVideo(index, "up")}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => moveVideo(index, "down")}
                      disabled={index === videos.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Thumbnail or generate button */}
                  <div className="w-24 h-32 rounded bg-muted flex items-center justify-center overflow-hidden">
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => generateThumbnail(video.id, video.video_url)}
                        disabled={generatingThumbnail === video.id}
                        className="flex flex-col gap-1"
                      >
                        <Image className="w-5 h-5" />
                        <span className="text-xs">Generate</span>
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="font-semibold">{video.title}</h3>
                    <p className="text-sm text-muted-foreground">{video.industry} • {video.location}</p>
                    {video.description && <p className="text-sm text-muted-foreground mt-1">{video.description}</p>}
                  </div>
                  
                  {/* Action buttons */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEditModal(video)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDelete(video.id, video.video_url, video.thumbnail_url)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </TabsContent>

          {/* Hero Images Tab */}
          <TabsContent value="hero" className="space-y-6">
            <div className="bg-card p-6 rounded-xl border border-border">
              <h2 className="text-xl font-heading font-bold mb-4">Manage Hero Section Images</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Upload new images for the hero carousel. Recommended size: 1080x1920 (9:16 vertical format)
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {heroImages.map((image, index) => (
                  <div key={image.id} className="border border-border rounded-lg p-4 space-y-3">
                    <div className="aspect-[9/16] bg-muted rounded-lg overflow-hidden">
                      <img 
                        src={`/src/assets/${image.path}`}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/1080x1920?text=Hero+Image+' + (index + 1);
                        }}
                      />
                    </div>
                    <div>
                      <p className="font-medium text-sm mb-2">{image.name}</p>
                      <p className="text-xs text-muted-foreground mb-3">Current: {image.path}</p>
                      <Input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleHeroImageUpload(image.id, file);
                        }}
                        disabled={uploadingHeroImage}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">📝 Important Note:</p>
                <p className="text-xs text-muted-foreground">
                  After uploading new images, you'll need to copy them to <code className="bg-background px-1 py-0.5 rounded">src/assets/</code> folder 
                  and update the imports in <code className="bg-background px-1 py-0.5 rounded">src/components/Hero.tsx</code> to see changes in production.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Edit Dialog (Portfolio) */}
        <Dialog open={!!editingVideo} onOpenChange={(open) => !open && setEditingVideo(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Video</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <Input
                    value={editFormData.title}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Industry</label>
                  <Input
                    value={editFormData.industry}
                    onChange={(e) => setEditFormData({ ...editFormData, industry: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Location</label>
                  <Input
                    value={editFormData.location}
                    onChange={(e) => setEditFormData({ ...editFormData, location: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <Input
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">New Thumbnail (Optional)</label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setEditThumbnailFile(e.target.files?.[0] || null)}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditingVideo(null)}>
                  Cancel
                </Button>
                <Button onClick={handleEditSubmit} disabled={uploading}>
                  {uploading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
